'use client'

import { useState, useEffect } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react'
import {
  Bars3Icon,
  HomeIcon,
  XMarkIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  DocumentCurrencyDollarIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  FolderIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom';

// Update the navigation array to include correct hrefs
const navigation = [
  { name: 'Dashboard', href: '/tutor/dashboard', icon: HomeIcon, component: 'Dashboard' },
  { name: 'Inbox', href: '/tutor/inbox', icon: ChatBubbleLeftRightIcon, component: 'Inbox' },
  { name: 'Schedule', href: '/tutor/schedule', icon: CalendarDaysIcon, component: 'Schedule' },
  { name: 'Bookings', href: '/tutor/bookings', icon: CalendarDaysIcon, component: 'Bookings' },
  { name: 'Recordings', href: '/tutor/recordings', icon: VideoCameraIcon, component: 'Recordings' },
  { name: 'Opportunities', href: '/tutor/opportunities', icon: BriefcaseIcon, component: 'Opportunity' },
  { name: 'Earnings', href: '/tutor/earnings', icon: DocumentCurrencyDollarIcon, component: 'Earnings' },
  { name: 'Invoice', href: '/tutor/invoice', icon: AcademicCapIcon, component: 'Invoice' },
  { name: 'File Sharing', href: '/tutor/file-sharing', icon: FolderIcon, component: 'FileSharing' },
]

const onboarding = [
  { id: 1, name: 'Basic Information', href: '/tutor/onboarding/basicinfo', initial: 'B', current: false },
  { id: 2, name: 'Select subjects', href: '/tutor/onboarding/selectsubjects', initial: 'S', current: false },
  { id: 3, name: 'Education', href: '/tutor/onboarding/education', initial: 'P', current: false },
  { id: 4, name: 'Bank details', href: '/tutor/onboarding/bankdetails', initial: 'B', current: false },
  // { id: 5, name: 'Contract', href: '/tutor/onboarding/contract', initial: 'C', current: false },
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function Sidebar({ activeComponent, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [currentItem, setCurrentItem] = useState(navigation[0].component)

  useEffect(() => {
    const active = navigation.find(item => item.component === activeComponent)
    if (active) {
      setCurrentItem(active.component)
    }
  }, [activeComponent])

  return (
    <>
      {/*
        This example requires updating your template:

        ```
        <html class="h-full bg-gray-50">
        <body class="h-full">
        ```
      */}
      <div>
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
                <div className="flex h-16 shrink-0 items-center">
                  <img
                    alt="Mathsforce logo"
                    src={`${process.env.PUBLIC_URL}/mathsforce-high-resolution-logo.png`}
                    className="h-32 w-auto"
                  />
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <a
                              href={item.href}
                              className={classNames(
                                item.component === currentItem
                                  ? 'bg-gray-50 text-indigo-600'
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                                'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                              )}
                            >
                              <item.icon
                                aria-hidden="true"
                                className={classNames(
                                  item.component === currentItem ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                                  'h-6 w-6 shrink-0',
                                )}
                              />
                              {item.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li>
                      <div className="text-xs font-semibold leading-6 text-gray-400">Onborading</div>
                      <ul role="list" className="-mx-2 mt-2 space-y-1">
                        {onboarding.map((step) => (
                          <li key={step.name}>
                            <a
                              href={step.href}
                              className={classNames(
                                step.current
                                  ? 'bg-gray-50 text-indigo-600'
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                                'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                              )}
                            >
                              {/* <span
                                className={classNames(
                                  step.current
                                    ? 'border-indigo-600 text-indigo-600'
                                    : 'border-gray-200 text-gray-400 group-hover:border-indigo-600 group-hover:text-indigo-600',
                                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium',
                                )}
                              >
                                {step.initial}
                              </span> */}
                              <span className="truncate">{step.name}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
            <div className="flex h-16 shrink-0 items-center">
              <img
                alt="Mathsforce logo"
                src={`${process.env.PUBLIC_URL}/mathsforce-high-resolution-logo.png`}
                className="h-32 w-auto"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <a
                          href={item.href}
                          className={classNames(
                            item.component === currentItem
                              ? 'bg-gray-50 text-indigo-600'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                            'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                          )}
                        >
                          <item.icon
                            aria-hidden="true"
                            className={classNames(
                              item.component === currentItem ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                              'h-6 w-6 shrink-0',
                            )}
                          />
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
                <li>
                  <div className="text-xs font-semibold leading-6 text-gray-400">Onborading</div>
                  <ul role="list" className="-mx-2 mt-2 space-y-1">
                    {onboarding.map((step) => (
                      <li key={step.name}>
                        <a
                          href={step.href}
                          className={classNames(
                            step.current
                              ? 'bg-gray-50 text-indigo-600'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                            'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                          )}
                        >
                          {/* <span
                            className={classNames(
                              step.current
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-gray-200 text-gray-400 group-hover:border-indigo-600 group-hover:text-indigo-600',
                              'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium',
                            )}
                          >
                            {step.initial}
                          </span> */}
                          <span className="truncate">{step.name}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
                {/* <li className="-mx-6 mt-auto">
                  <a
                    href="#"
                    className="flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-gray-900 hover:bg-gray-50"
                  >
                    <img
                      alt=""
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                      className="h-8 w-8 rounded-full bg-gray-50"
                    />
                    <span className="sr-only">Your profile</span>
                    <span aria-hidden="true">Tom Cook</span>
                  </a>
                </li> */}
              </ul>
            </nav>
          </div>
        </div>

        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
          <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 text-gray-700 lg:hidden">
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>
          <div className="flex-1 text-sm font-semibold leading-6 text-gray-900">Dashboard</div>
          <a href="#">
            <span className="sr-only">Your profile</span>
            <img
              alt=""
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
              className="h-8 w-8 rounded-full bg-gray-50"
            />
          </a>
        </div>

        <main className="py-10 lg:pl-72">
          <div className="px-4 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    </>
  )
}
